import React, { createContext, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axios'; // Import the Axios instance

const AuthContext = createContext(null);

// Campus Ministry forms live at /campus-ministry/<form>/register|payment.
const isCampusMinistryPublicPath = (pathname) => /^\/campus-ministry\/[a-z0-9-]+\/(register|payment)$/.test(pathname);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const checkAuth = async () => {
    try {
      // const response = await fetch('/api/auth/status', {
      //   credentials: 'include' // Important for cookies
      // });
      const response = await axiosInstance.get('/auth/status'); // Use axiosInstance
      
      // if (!response.ok) {
      //   throw new Error('Authentication check failed');
      // }

      const data = response.data; // Axios provides data directly in response.data
      setIsAuthenticated(data.isAuthenticated);
      setUser(data.user);
      setError(null);

      // If not authenticated and not on a public page, redirect to login
      const publicPaths = ['/login', '/re/register', '/re/payment', '/ocia/register', '/ocia/payment'];
      if (!data.isAuthenticated && !publicPaths.includes(window.location.pathname) && !isCampusMinistryPublicPath(window.location.pathname)) {
        navigate('/login');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      setIsAuthenticated(false);
      setUser(null);
      setError(error.message);
      // Only redirect to login if not on a public page
      const publicPaths = ['/login', '/re/register', '/re/payment', '/ocia/register', '/ocia/payment'];
      if (!publicPaths.includes(window.location.pathname) && !isCampusMinistryPublicPath(window.location.pathname)) {
        navigate('/login');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkAuth();
  }, [navigate]);

  const login = async (googleResponse) => {
    try {
      // const response = await fetch('/api/auth/google', {
      //   method: 'POST',
      //   headers: {
      //     'Content-Type': 'application/json',
      //   },
      //   credentials: 'include',
      //   body: JSON.stringify({ token: googleResponse.tokenId })
      // });
      await axiosInstance.post('/auth/google', { token: googleResponse.tokenId }); // Use axiosInstance

      // if (!response.ok) { // Axios handles non-2xx errors by throwing
      //   throw new Error('Login failed');
      // }

      await checkAuth();
      navigate('/religious-education');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const logout = async () => {
    try {
      // const response = await fetch('/api/auth/logout', {
      //   method: 'POST',
      //   credentials: 'include'
      // });
      await axiosInstance.post('/auth/logout'); // Use axiosInstance

      // if (!response.ok) { // Axios handles non-2xx errors by throwing
      //   throw new Error('Logout failed');
      // }

      setIsAuthenticated(false);
      setUser(null);
      navigate('/login');
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Permission helper functions
  const hasPermission = (permissionType) => {
    if (!user) return false;

    // Admins have all permissions
    if (user.isAdmin) return true;

    // Check specific permission
    const permissions = user.permissions || {};
    return Boolean(permissions[permissionType]);
  };

  const hasAnyPermission = (permissionTypes) => {
    return permissionTypes.some(permission => hasPermission(permission));
  };

  const canAccessCCD = () => hasPermission('ccdRegistrations');
  const canAccessParish = () => hasPermission('parishRegistrations');
  const canAccessStaff = () => hasPermission('staffManagement');
  const canAccessMass = () => hasPermission('massManagement');
  const canAccessOCIA = () => hasPermission('ociaRegistrations');
  const canAccessCampusMinistry = () => hasPermission('campusMinistryRegistrations');

  if (loading) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div>Loading...</div>
        {error && (
          <div style={{ color: 'red' }}>
            Error: {error}
          </div>
        )}
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{
      isAuthenticated,
      user,
      login,
      logout,
      error,
      checkAuth,
      hasPermission,
      hasAnyPermission,
      canAccessCCD,
      canAccessParish,
      canAccessStaff,
      canAccessMass,
      canAccessOCIA,
      canAccessCampusMinistry
    }}>
      {children}
    </AuthContext.Provider>
  );
}; 